/* eslint-disable no-console */
/* eslint-disable max-len */
/* eslint-disable no-plusplus */
import React, { useCallback, useEffect, useState, useContext } from 'react';
import _ from 'lodash';
import appConfig from 'app-config';
import PropTypes from 'prop-types';

import { TranslatorContext } from '@jutro/locale';

import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { useValidation } from '@xengage/gw-portals-validation-react'
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { MockUpUtil } from '@xengage/gw-portals-util-js'
import { Accordion } from '@jutro/legacy/components';

import { WMICScrollToError } from 'wmic-pe-components-platform-react';
import { useWizardModals, useDocumentTitle, WMICWizardSubmissionPage } from 'wmic-pe-portals-wizard-components-ui'
import { WMICCPLocationCoverages } from 'wmic-pe-capability-gateway-quoteandbind-cp-react';
import { WMICGLPropertyLineCoverages } from 'wmic-pe-capability-gateway-quoteandbind-gl-react';
import { FlowStepId, WMICValidationUtil, WMICVariousUtil } from 'wmic-pe-portals-utils-js';

import WMICCPCoverageUtil from "wmic-pe-capability-gateway-quoteandbind-cp-react/util/WMICCPCoverageUtil";

import { messages as commonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';
import metadata from './WMICPropertyPage.metadata.json5';
import messages from './WMICPropertyPage.messages';
import styles from './WMICPropertyPage.module.scss';

const LOCATION_PATH = 'lobData.commercialProperty.coverables.locations';

const MOCK_DATA_TO_REMOVE = [
    'bindData.contactPhone',
    'bindData.contactEmail'
];

const structureCustomQuote = (submissionVM, affectedQuote, clauses) =>
    // convert OfferingDTO to CustomQuotedDTO structure
     ({
        quote: affectedQuote,
        quoteID: submissionVM.quoteID.value,
        sessionUUID: submissionVM.sessionUUID.value,
        periodStart: submissionVM.baseData.periodStartDate.value,
        periodEnd: submissionVM.baseData.periodEndDate.value,
        coverages: clauses
    })
;

const getCustomQuote = (vm, lobPath, quotePath, lobName, filterChangedClauses = false) => {
    const lobOffering = _.get(vm, `${lobPath}.value`);
    const quoteOffering = _.get(vm, `${quotePath}.value`);

    let clausesToUpdate = {
        [lobName]: lobOffering.coverages
    };

    if (filterChangedClauses) {
        // eslint-disable-next-line max-len
        clausesToUpdate = ClausesUtil.structureClausesForServer(
            lobOffering.coverages,
            lobName,
            null
        );
    }

    return structureCustomQuote(vm, quoteOffering, clausesToUpdate);
};

function WMICPropertyPage(props) {
    const { wizardSnapshot, wizardData: submissionVM, updateWizardData, underwritingIssues } = props;
    const translator = useContext(TranslatorContext);
    const [staleQuoteBranchCode, setStaleQuoteBranchCode] = useState(undefined);
    const [availableCoverages, setAvailableCoverages] = useState();
    const [showErrors, setShowErrors] = useState(false);
    const [scrollToError, setScrollToError] = useState(false); // we toggle this when we want to scroll to the first error on the page

    const { CustomQuoteService } = useDependencies('CustomQuoteService');
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { setWizardLoading } = useWizardModals();
    const { authHeader } = useAuthentication();
    const { onValidate, disregardFieldValidation, initialValidation, registerInitialComponentValidation, isComponentValid } = useValidation("cpPropertyPage");
    const showQuoteStartDate = appConfig.showQuoteStartDateInHeader;
    const viewModelService = useContext(ViewModelServiceContext);
    const [initialSubmissionVM, setInitialSubmissionVM] = useState(undefined);

    useDocumentTitle(translator(messages.title), submissionVM);

    const validateTableForm = useCallback(() =>
        WMICValidationUtil.validatePageEntities(submissionVM, [`${LOCATION_PATH}`])
            && !WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.CP_PROPERTY)
    , [submissionVM]);

    useEffect(() => {
        registerInitialComponentValidation(validateTableForm);
    }, [registerInitialComponentValidation, validateTableForm]);

    useEffect(() => {
        if (isComponentValid) {
            setShowErrors(false);
        }
    }, [isComponentValid]);


    useEffect(() => {
        submissionVM.value.flowStepId_WMIC = FlowStepId.CP_PROPERTY;
    }, [submissionVM.value]);

    const getRemovedFieldsForLob = useCallback((lobName, lobPath, newSubmissionVM) => {

        let allRemovedFields = [];

        switch(lobName) {
            case 'commercialProperty':
                const removedFieldsFromLocationCoverages = ClausesUtil.getRemovedClausesID(
                    submissionVM,
                    newSubmissionVM,
                    `${lobPath}.coverages.locationCoverages`
                );

                const removedFieldsFromBuildingCoverages = ClausesUtil.getRemovedClausesID(
                    submissionVM,
                    newSubmissionVM,
                    `${lobPath}.coverages.buildingCoverages`
                );

                allRemovedFields = [
                    ...removedFieldsFromLocationCoverages,
                    ...removedFieldsFromBuildingCoverages
                ];

                break;
            case 'generalLiability':

                const removedFieldsFromPropertyLineCoverages = ClausesUtil.getRemovedClausesID(
                    submissionVM,
                    newSubmissionVM,
                    `${lobPath}.coverages.propertyLineCoverages`
                );

                allRemovedFields = [
                    ...removedFieldsFromPropertyLineCoverages,
                ];

                break;
            default:
                break;
        }

        return allRemovedFields;
    }, [submissionVM]);

    const onClauseChange = useCallback(
        (_basePath, lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, quotePath, lobName, true);
            setWizardLoading(true);

            return CustomQuoteService.updateCustomQuoteCoveragesAndQuoteData(customQuote, submissionVM.value, authHeader).then(
                (response) => {
                    const newSubmissionVM = viewModelService.clone(submissionVM);
                    newSubmissionVM.value = response;

                    const allRemovedFields = getRemovedFieldsForLob(lobName, lobPath, newSubmissionVM);

                    disregardFieldValidation(allRemovedFields);
                    updateWizardData(newSubmissionVM);
                }
            ).finally(() => {
                setWizardLoading(false);
            });
        },
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [submissionVM, setWizardLoading, CustomQuoteService, authHeader, viewModelService, getRemovedFieldsForLob, disregardFieldValidation]
    );

    const getCoverages = (submission) => {
        // putting ID into an object as the Jutro table component expects an object
        const structureClauseTData = (coverage) => ({
            coverageUniqueID: coverage.publicID,
            coverageCategoryCode: coverage.category_WMIC,
            coverageCategoryDisplayName: coverage.category_DisplayName_WMIC
        });

        const offerings = _.get(submission, 'lobData.commercialProperty.offerings.value');

        const allLocationCoverages = offerings.flatMap((offering) => offering.coverages.locationCoverages);
        const allBuildingCoverages = offerings.flatMap((offering) => offering.coverages.buildingCoverages);

        const locationCoverages = allLocationCoverages.map((locationCoverageDTO) => ({
                ...locationCoverageDTO,
                coverages: _.uniqBy(locationCoverageDTO.coverages.map(structureClauseTData), 'coverageUniqueID')
            }));

        const buildingCoverages = allBuildingCoverages.map((buildingCoverageDTO) => ({
                ...buildingCoverageDTO,
                coverages: _.uniqBy(buildingCoverageDTO.coverages.map(structureClauseTData), 'coverageUniqueID')
            }));

        return {
            locationCoverages,
            buildingCoverages
        };
    };

    useEffect(() => {
        if (submissionVM) {
            setAvailableCoverages(getCoverages(submissionVM));
        }
    }, []);

    const syncCoverages = useCallback(
        (value, changedPath, lobPath, quotePath) => {
            const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
            return onClauseChange(basePath, lobPath, lobPath);
        },
        [onClauseChange]
    );

    const changeSubmission = useCallback(
        (value, changedPath) => {
            updateWizardData(ClausesUtil.setClauseValue(submissionVM, value, changedPath));
        },
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [submissionVM]
    );

    const changeSubmissionAndSync = useCallback(
        (value, changedPath, lobPath, quotePath) => {
            changeSubmission(value, changedPath);
            return syncCoverages(value, changedPath, lobPath, quotePath);
        },
        [changeSubmission, syncCoverages]
    );

    const resetQuote = useCallback(
        (lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(initialSubmissionVM, lobPath, quotePath, lobName);

            return CustomQuoteService.forceUpdateCustomQuoteCoveragesAndQuoteData(customQuote, initialSubmissionVM.value, authHeader).then(
                (response) => {
                    const newSubmissionVM = viewModelService.clone(submissionVM);
                    newSubmissionVM.value = response;
                    updateWizardData(newSubmissionVM);
                }
            );
        },
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [initialSubmissionVM, submissionVM, CustomQuoteService, authHeader]
    );

    const recalculate = useCallback(
        (lobPath, quotePath) => {
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, quotePath, lobName);

            return CustomQuoteService.updateCustomQuoteAndQuoteData(customQuote, authHeader).then(
                (response) => {
                    const newSubmissionVM = viewModelService.clone(submissionVM);
                    newSubmissionVM.value = response;
                    updateWizardData(newSubmissionVM);
                    return response;
                }
            );
        },
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [submissionVM, CustomQuoteService, authHeader]
    );

    const handlePrint = useCallback(() => {
        window.print();
    }, []);

    const buyNow = useCallback(
        async (lobPath, quotePath) => {
            const lobOffering = _.get(submissionVM, `${lobPath}.value`);
            const quoteOffering = _.get(submissionVM, `${quotePath}.value`);
            const quoteID = _.get(submissionVM, 'quoteID.value');
            const sessionUUID = _.get(submissionVM, 'sessionUUID.value');

            await CustomQuoteService.setSelectedVersionOnSubmission(
                quoteID,
                lobOffering.branchName,
                sessionUUID,
                authHeader
            );

            // set chosen quote
            _.set(submissionVM, 'bindData.chosenQuote.value', quoteOffering.publicID);
            submissionVM.value = MockUpUtil.cleanUpMockedProperties(
                submissionVM.value,
                'quote.cp',
                ...MOCK_DATA_TO_REMOVE
            );
            updateWizardData(submissionVM);
        },
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [submissionVM, CustomQuoteService, authHeader]
    );

    const getCustomTerms = (clause) => null

    const onStaleQuoteBranchCode = useCallback(() => staleQuoteBranchCode, [staleQuoteBranchCode]);

    const commonOverrides = {
        'quoteID': submissionVM.quoteID.value,
        'changeSubmissionAndSync': changeSubmissionAndSync,
        'changeSubmission': changeSubmission,
        'onClauseChange': onClauseChange,
        'syncCoverages': syncCoverages,
        'recalculate': recalculate,
        'resetQuote': resetQuote,
        'onStaleQuoteBranchCode': onStaleQuoteBranchCode,
        'underwritingIssues': underwritingIssues,
        'onValidate': onValidate,
        'getCustomTerms': getCustomTerms,
        'buyNow' : buyNow,
    }


    const _iterableProps = () => {
        const locationOverrides = _.get(submissionVM, `${LOCATION_PATH}.children`).map((location, index) => {
            const locationCoverages = WMICCPCoverageUtil.getCoveragesForLocation(
                _.get(submissionVM, 'lobData.commercialProperty'),
                location.publicID.value
            );

            const locationOverride = {
                [`locationCoveragesIterableComponent${index}`]: {
                    'locationVM': location,
                    'locationIndex': index,
                    'baseData': submissionVM.baseData,
                    locationCoverages,
                    jobVM: submissionVM,
                    ...commonOverrides
                }
            }

            return Object.assign({}, locationOverride);
        })

        return Object.assign({}, ...locationOverrides);
    }

    const overrideProps = {
        startDate: {
            visible: showQuoteStartDate
        },
        print: {
            visible: !showQuoteStartDate
        },
        locationCoveragesIterableAccordionContainer: {
            data: _.get(submissionVM, `${LOCATION_PATH}.children`, [])
        },
        propertyLineCoverage: {
            ...commonOverrides,
            jobVM: submissionVM,
        },
        ..._iterableProps()
    };

    useEffect(() => {
        const offerings = _.get(submissionVM, 'quoteData.offeredQuotes.value');
        const draftOffers = _.filter(offerings, ['status', 'Draft']);
        const draftBranchCodes = _.map(draftOffers, 'branchCode');
        setStaleQuoteBranchCode(draftBranchCodes);
        // Store the initial SubmissionVM when component is mounted
        setInitialSubmissionVM(viewModelService.clone(wizardSnapshot));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onNext = useCallback(async () => {
        try {
            if (!isComponentValid) {
                setShowErrors(true);
                setScrollToError(Date.now());
                return false;
            }

            setWizardLoading(true);
            const newSubmissionVM = viewModelService.clone(submissionVM);

            _.unset(newSubmissionVM.value, 'bindData');

            submissionVM.value = await LoadSaveService.updateDraftSubmissionWithLOBData(newSubmissionVM.value, authHeader)

            if (WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.CP_PROPERTY)) {
                WMICVariousUtil.scrollToTop();
                return false
            }

            return submissionVM;
        } finally {
            setWizardLoading(false);
        }
    }, [LoadSaveService, authHeader, isComponentValid, setWizardLoading, submissionVM, viewModelService]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onBuyNow: (lobPath, quotePath) => buyNow(lobPath, quotePath).then(onNext),
            onStaleQuoteBranchCode,
            onRecalculate: recalculate,
            onResetQuote: resetQuote,
            onChangeSubmissionAndSync: changeSubmissionAndSync,
            onChangeSubmission: changeSubmission,
            onSyncCoverages: syncCoverages,
            onPrint: handlePrint
        },
        resolveComponentMap: {
            WMICCPLocationCoverages,
            WMICGLPropertyLineCoverages,
            Accordion
        }
    };

    return (
        <WMICWizardSubmissionPage
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            cancelLabel={commonMessages.saveAndExit}
            onNext={onNext}
            flowStepId={FlowStepId.CP_PROPERTY}
        >
            <WMICScrollToError counter={scrollToError}/>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}
            />
        </WMICWizardSubmissionPage>
    );
}

WMICPropertyPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    ...wizardProps
};

export default WMICPropertyPage;
